import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, MapPin, Clock, Send, Loader2, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { BRANDING } from "@/lib/branding.config";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    await new Promise(r => setTimeout(r, 1000));
    toast.success("Message sent! We'll get back to you soon.");
    setForm({ name: "", email: "", subject: "", message: "" });
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-navy-deepest text-slate-200">
      <header className="sticky top-0 z-50 w-full border-b border-slate-800/60 bg-navy-deepest/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-300 hover:text-gold hover:bg-transparent">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <Link to="/" className="flex items-center gap-3">
            <img src="/brand/bicarra-icon.svg" alt="Bicarra" className="w-8 h-8 rounded-lg" />
            <div className="leading-tight">
              <div className="font-bold text-base tracking-tight text-white">BICARRA</div>
              <div className="text-[10px] tracking-[0.18em] text-gold uppercase">by {BRANDING.endorser.short}</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-6 ml-auto text-sm">
            <Link to="/" className="text-slate-300 hover:text-gold transition">Home</Link>
            <Link to="/pricing" className="text-slate-300 hover:text-gold transition">Pricing</Link>
            <Link to="/contact-us" className="text-gold">Contact</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-16" data-testid="contact-page">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left — Info */}
          <div>
            <span className="inline-block text-xs font-bold tracking-[0.18em] text-gold uppercase mb-4">Get in touch</span>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-white mb-4">Contact Us</h1>
            <p className="text-slate-300/80 mb-10 max-w-md">
              Have a question or need help? Our Malaysia-based team is here to assist your business.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-none border border-gold/40 bg-navy-deep flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <p className="font-medium text-white">Email</p>
                  <p className="text-slate-300/80">{BRANDING.contact.supportEmail}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-none border border-gold/40 bg-navy-deep flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <p className="font-medium text-white">Location</p>
                  <p className="text-slate-300/80">Kuala Lumpur, Malaysia</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-none border border-gold/40 bg-navy-deep flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <p className="font-medium text-white">Support Hours</p>
                  <p className="text-slate-300/80">Mon-Fri, 9am-6pm MYT (GMT+8)</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-none border border-gold/40 bg-navy-deep flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <p className="font-medium text-white">Live chat</p>
                  <p className="text-slate-300/80">Click the Bicarra widget in the bottom-right</p>
                </div>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-slate-800/60">
              <p className="text-[11px] tracking-[0.18em] text-slate-400 uppercase mb-1">Powered by</p>
              <p className="font-serif italic text-xl text-gold">{BRANDING.endorser.full}</p>
            </div>
          </div>

          {/* Right — Form */}
          <div className="bg-navy-deep border border-slate-700/60 p-8 lg:p-10">
            <h2 className="font-serif text-2xl md:text-3xl text-white mb-6">Send us a message</h2>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[11px] tracking-[0.18em] text-slate-400 uppercase">Name</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="John Doe"
                    required
                    className="rounded-none bg-navy-deepest border-slate-700 text-white placeholder:text-slate-500 focus:border-gold"
                    data-testid="contact-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[11px] tracking-[0.18em] text-slate-400 uppercase">Email</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="john@example.com"
                    required
                    className="rounded-none bg-navy-deepest border-slate-700 text-white placeholder:text-slate-500 focus:border-gold"
                    data-testid="contact-email"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[11px] tracking-[0.18em] text-slate-400 uppercase">Subject</Label>
                <Input
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="How can we help?"
                  required
                  className="rounded-none bg-navy-deepest border-slate-700 text-white placeholder:text-slate-500 focus:border-gold"
                  data-testid="contact-subject"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[11px] tracking-[0.18em] text-slate-400 uppercase">Message</Label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us more..."
                  required
                  className="rounded-none bg-navy-deepest border-slate-700 text-white placeholder:text-slate-500 focus:border-gold min-h-[150px]"
                  data-testid="contact-message"
                />
              </div>
              <Button
                type="submit"
                className="w-full rounded-none bg-gold text-navy-deepest hover:bg-gold-soft transition font-bold tracking-wide"
                disabled={sending}
                data-testid="contact-submit"
              >
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send Message
              </Button>
            </form>
          </div>
        </div>
      </main>

      <footer className="border-t border-slate-800/60 py-10 text-center text-sm text-slate-400">
        {BRANDING.legal.copyright}
      </footer>
    </div>
  );
}
